import React, { Component } from 'react';
import { isEqual } from 'lodash';
import PropTypes from 'prop-types';
import memoize from 'memoize-one';
import ExportableTable from 'containers/tables/ExportableTable';
import CellDiv from 'components/molecules/tables/components/CellDiv';
import ContentTile from 'components/atoms/layout/ContentTile';
import ContentHeader from 'components/atoms/layout/ContentHeader';
import FilterBar from 'components/atoms/tables/FilterBar';
import FilterLabel from 'components/atoms/tables/FilterLabel';
import ToggleSwitch from 'components/atoms/form/ToggleSwitch';
import Pagination, { RowsPerPageDefault } from 'components/molecules/tables/components/Pagination';
import contentPadding from 'helpers/contentPadding';
import { generateFooterData } from 'helpers/tables';
import { getActivitiesSettings } from 'helpers/objectiveSettings';
import { convertToHeaderDisplayValue } from 'helpers/strings';


const cellStyle = {
    backgroundColor: 'rgba(200, 200, 200, 0.15)'
};

class ActivitiesTable extends Component {
    constructor(props) {
        super(props);

        this.prepareActivityTableData = memoize(ActivitiesTable.prepareActivityTableData);
        this.state = {
            showAttribution: false
        };
    }

    shouldComponentUpdate(nextProps, nextState) {
        return !isEqual(nextProps, this.props) || !isEqual(nextState, this.state);
    }

    static prepareActivityTableData(activityKeys, activityData, filterField) {
        return activityKeys.map((key) => {
            if (activityData[key].statistics[filterField] > 0) {
                return {
                    activityName: key,
                    ...activityData[key].statistics
                };
            }

            return false;
        }).filter(record => record);
    }

    render() {
        const {
            title,
            exportName,
            data,
            objectives,
            showTotalActivities,
            entityObjectiveLabelCode,
            filterField = 'ecpa_activities',
            caption
        } = this.props;
        const { showAttribution } = this.state;
        const processedData = this.prepareActivityTableData(data.keys, data.data, filterField);
        const footerData = generateFooterData({ data: processedData, filteredKeys: ['activityName'] });
        const includesECPA = objectives.includes('ecpa');

        const handleShowAttribution = () => {
            this.setState({
                showAttribution: !this.state.showAttribution
            });
        };

        const {
            singularTerm,
            pluralTerm,
            primaryActivityFieldTerm,
        } = getActivitiesSettings(objectives[0], entityObjectiveLabelCode);

        let defaultSort = {
            id: 'total_activities',
            desc: true
        };

        const columns = [
            {
                Header: singularTerm.toUpperCase(),
                accessor: 'activityName',
                Cell: row => <CellDiv value={row.value} />,
                Footer: (
                    <CellDiv value='Total' strong />
                )
            }
        ];


        if (showTotalActivities) {
            defaultSort = { id: 'total_activities', desc: true };
            const showTotalActivityColumns = [
                {
                    Header: showAttribution ? 'TOTAL' : 'TOTAL ACTIVITIES',
                    accessor: 'total_activities',
                    type: 'number',
                    style: includesECPA ? cellStyle : null,
                    Cell: row => <CellDiv value={row.value} type="number" />,
                    Footer: (
                        <CellDiv value={footerData.total_activities} type="number" strong />
                    )
                }
            ];

            if (showAttribution) {
                showTotalActivityColumns.unshift(
                    {
                        Header: 'POST-IMPRESSION',
                        accessor: 'total_post_impression_activity_count',
                        type: 'number',
                        style: includesECPA ? cellStyle : null,
                        Cell: row => <CellDiv value={row.value} type="number" />,
                        Footer: (
                            <CellDiv value={footerData.total_post_impression_activity_count} type="number" strong />
                        )
                    },
                    {
                        Header: 'POST-CLICK',
                        accessor: 'total_post_click_activity_count',
                        type: 'number',
                        style: includesECPA ? cellStyle : null,
                        Cell: row => <CellDiv value={row.value} type="number" />,
                        Footer: (
                            <CellDiv value={footerData.total_post_click_activity_count} type="number" strong />
                        )
                    }
                );
            }
            // if showing attribution, we need have a header row and 3 columns,
            // otherwise it is just the ECPA Activities column
            columns.push(showAttribution ? {
                Header: `TOTAL ${pluralTerm.toUpperCase()}`,
                columns: showTotalActivityColumns
            } : showTotalActivityColumns[0]);
        }

        if (includesECPA) {
            defaultSort = { id: 'ecpa_activities', desc: true };
            const includesECPAColumns = [
                {
                    Header: showAttribution ? 'TOTAL' : convertToHeaderDisplayValue(primaryActivityFieldTerm),
                    accessor: 'ecpa_activities',
                    type: 'number',
                    Cell: row => <CellDiv value={row.value} type="number" />,
                    Footer: (
                        <CellDiv value={footerData.ecpa_activities} type="number" strong />
                    )
                }
            ];
            if (showAttribution) {
                includesECPAColumns.unshift(
                    {
                        Header: 'POST-IMPRESSION',
                        accessor: 'ecpa_post_impression_activity_count',
                        type: 'number',
                        Cell: row => <CellDiv value={row.value} type="number" />,
                        Footer: (
                            <CellDiv value={footerData.ecpa_post_impression_activity_count} type="number" strong />
                        )
                    },
                    {
                        Header: 'POST-CLICK',
                        accessor: 'ecpa_post_click_activity_count',
                        type: 'number',
                        Cell: row => <CellDiv value={row.value} type="number" />,
                        Footer: (
                            <CellDiv value={footerData.ecpa_post_click_activity_count} type="number" strong />
                        )
                    }
                );
            }
            // if showing attribution, we need have a header row and 3 columns,
            // otherwise it is just the Total Activities column
            columns.push(showAttribution ? {
                Header: convertToHeaderDisplayValue(primaryActivityFieldTerm),
                columns: includesECPAColumns
            } : includesECPAColumns[0]);
        }

        return (
            <ContentTile padding={contentPadding('table')}>
                <ContentHeader tableData={processedData} exportName={exportName} type={'table'}>{title}</ContentHeader>
                <FilterBar>
                    <ToggleSwitch onClick={handleShowAttribution} value={!!showAttribution} scale="0.5" />
                    <FilterLabel>Show Impression/Click Attribution</FilterLabel>
                </FilterBar>
                <ExportableTable
                    key={1}
                    title={title}
                    headerDepth={2}
                    exportName={exportName}
                    data={processedData}
                    columns={columns}
                    getTfootProps={() => ({ style: { backgroundColor: '#E2E2E2', fontWeight: 'bold', boxShadow: 'none' } })}
                    getTheadThProps={() => ({ style: { outline: 0 } })}
                    defaultPageSize={RowsPerPageDefault}
                    exportRows={processedData.length}
                    showPagination={processedData.length > RowsPerPageDefault}
                    PaginationComponent={Pagination}
                    defaultSorted={[defaultSort]}
                    minRows={processedData.length < RowsPerPageDefault ?
                        processedData.length : RowsPerPageDefault}
                    className="-highlight"
                />
                {caption && <p style={{ padding: '10px 0px 0px 8px' }}>{caption}</p>}
            </ContentTile>
        );
    }
}

ActivitiesTable.propTypes = {
    data: PropTypes.shape({
        keys: PropTypes.arrayOf(PropTypes.string).isRequired,
        data: PropTypes.object.isRequired
    }),
    title: PropTypes.string,
    filterField: PropTypes.string,
    objectives: PropTypes.arrayOf(PropTypes.string),
    showTotalActivities: PropTypes.bool,
    entityObjectiveLabelCode: PropTypes.string,
    caption: PropTypes.string
};

export default ActivitiesTable;



// WEBPACK FOOTER //
// ./src/components/molecules/tables/ActivitiesTable.js